import React, { Component, PropTypes } from 'react';
import { Toggle, RaisedButton, DropDownMenu, Slider } from 'material-ui';
import * as Icons from 'components/Material/SvgIcons';
import PageModule from 'components/PageModule';

// https://github.com/miguelmota/audio-oscilloscope

window.AudioContext =
  window.AudioContext || window.webkitAudioContext || window.mozAudioContext;
window.URL = window.URL || window.webkitURL || window.mozURL;
navigator.getUserMedia =
  navigator.getUserMedia ||
  navigator.webkitGetUserMedia ||
  navigator.mozGetUserMedia;

export default class Oscilloscope extends PageModule {
  static readable = 'Oscilloscope';
  static icon = 'ImageExposure';
  static type = 1002;
  static public = false;
  static active = true;

  audioContext = null;
  analyser = null;
  bufferLength = 0;
  dataArray = [];
  canvasContext = null;
  width = 400;
  height = 400;
  stream = null;

  state = {
    powerOn: false,
    accumulation: false,
    sync: {
      mode: 3,
      vDiff: 0.1,
      tDiff: 3
    },
    autoGain: false
  };

  onInit() {
    this.width = this.refs.container.getBoundingClientRect().width;

    try {
      this.audioContext = new AudioContext();
    } catch (e) {
      alert('No audio input found.');
    }
  }

  draw() {
    if (!this.state.powerOn) {
      return;
    }

    let dataArray = this.dataArray;
    let bufferLength = this.bufferLength;
    let ctx = this.canvasContext;
    let w = this.width;
    let h = this.height;

    if (this.analyser) {
      this.analyser.getByteTimeDomainData(dataArray);
    }

    ctx.fillRect(0, 0, w, h);

    ctx.beginPath();

    // 'bufferLength' is usually 1024
    let sliceWidth = w * 1.0 / bufferLength;
    let x = 0;

    if (!bufferLength) {
      ctx.moveTo(0, this.height / 2);
    }

    let risingFront = false;
    let fallingFront = false;
    let zeroTrigger = false;
    const syncVDiff = this.state.sync.vDiff;

    // normalize
    const maxV = Math.max.apply(null, dataArray),
      minV = Math.min.apply(null, dataArray),
      spread = maxV - minV,
      semiA = Math.max(maxV / 128, minV / 128);

    // console.log(maxV, minV)

    for (var i = 0; i < bufferLength; i++) {
      switch (this.state.sync.mode) {
        case 0:
          break;

        case 1:
          // check for rising
          if (
            !risingFront &&
            i > 5 &&
            (dataArray[i - 5] - dataArray[i]) / spread > syncVDiff
          ) {
            x = 0;
            risingFront = true;
            ctx.fillRect(0, 0, w, h);
            ctx.beginPath();
            ctx.moveTo(0, this.height / 2);
          }
          break;

        case 2:
          // check for falling
          if (
            !fallingFront &&
            i > 5 &&
            (dataArray[i - 5] - dataArray[i]) / spread < syncVDiff
          ) {
            x = 0;
            fallingFront = true;
            ctx.fillRect(0, 0, w, h);
            ctx.beginPath();
            ctx.moveTo(0, this.height / 2);
          }
          break;

        case 3:
          // check for zero + increase
          if (
            !zeroTrigger &&
            i > 0 &&
            dataArray[i - 1] == 128 &&
            dataArray[i] < dataArray[i - 1]
          ) {
            x = 0;
            zeroTrigger = true;
            ctx.fillRect(0, 0, w, h);
            ctx.beginPath();
            ctx.moveTo(0, this.height / 2);
          }
          break;
      }

      let v = dataArray[i] - 128.0;

      if (this.state.autoGain) {
        v *= 128 / spread;
      }

      let y = h / 2 + v;

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }

      x += sliceWidth;
    }

    ctx.lineTo(w, h / 2);
    ctx.stroke();

    window.requestAnimationFrame(::this.draw);
  }

  addSource(streamSource) {
    this.audioContext = streamSource.context;
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 2048;
    this.bufferLength = this.analyser.frequencyBinCount;
    this.source = this.audioContext.createBufferSource();
    this.dataArray = new Uint8Array(this.bufferLength);
    this.analyser.getByteTimeDomainData(this.dataArray);
    streamSource.connect(this.analyser);
  }

  togglePower() {
    const power = !this.state.powerOn;
    this.setState({ powerOn: power });

    if (power) {
      this.setState({ accumulation: false });

      this.refs.canvas.width = this.width;
      this.refs.canvas.height = this.height;

      this.canvasContext = this.refs.canvas.getContext('2d');
      this.canvasContext.fillStyle = 'white';
      this.canvasContext.strokeStyle = 'red';
      this.canvasContext.lineWidth = 1;

      navigator.getUserMedia(
        { audio: true },
        stream => {
          this.stream = stream;
          this.addSource(this.audioContext.createMediaStreamSource(stream));
        },
        error => {
          console.error(error);
        }
      );
    } else {
      if (this.stream) {
        this.stream.getTracks()[0].stop();
      }
    }
  }

  toggleAccumulation() {
    const accumulation = !this.state.accumulation;
    this.setState({ accumulation: accumulation });
    this.canvasContext.fillStyle = accumulation ? 'rgba(0,0,0,0)' : 'white';
  }

  toggleAutoGain() {
    const autoGain = !this.state.autoGain;
    this.setState({ autoGain: autoGain });
  }

  changeSyncMode(e, i) {
    let sync = this.state.sync;
    sync.mode = i;
    this.setState({ sync: sync });
  }

  changeSyncVDiff(e, v) {
    let sync = this.state.sync;
    sync.vDiff = v;
    this.setState({ sync: sync });
  }

  renderEditMode(data) {
    return <div>Device is used in reader mode.</div>;
  }

  renderDetailMode(data) {
    let menuItems = [
      { payload: '0', text: 'No sync' },
      { payload: '1', text: 'Raising front sync' },
      { payload: '2', text: 'Falling front sync' },
      { payload: '3', text: 'Zero cross sync' }
    ];

    this.draw();

    return (
      <div ref="container">
        <div>
          <canvas ref="canvas" />
        </div>
        <div style={{ padding: '20px' }}>
          <RaisedButton
            label={'Power ' + (this.state.powerOn ? 'off' : 'on')}
            onTouchTap={::this.togglePower}
          />
          <hr />
          <Toggle
            defaultToggled={this.state.accumulation}
            label="Accumulation"
            onToggle={::this.toggleAccumulation}
          />
          <Toggle
            defaultToggled={this.state.autoGain}
            label="Auto gain"
            onToggle={::this.toggleAutoGain}
          />
          <DropDownMenu
            menuItems={menuItems}
            onChange={::this.changeSyncMode}
          />
          <Slider
            name="changeSyncVDiffSlider"
            defaultValue={0.1}
            max={0.5}
            onChange={::this.changeSyncVDiff}
          />
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/js/plugins/PageModules/Oscilloscope.js